import React, { useState, useEffect, useMemo, memo } from 'react'
import { FaBrain, FaTasks, FaStar, FaLock, FaTrash, FaClipboardList, FaCheck, FaRegCircle } from 'react-icons/fa'
import { LuSparkles } from 'react-icons/lu'
import { Heading, Text, Stack, Icon, Button, Input, DateChooser, TimeChooser, Grid, Radio, RadioGroup, Checkbox, Box, Select, Badge, Alert, SimpleAccordion, Tag, SimpleModal, SimpleTable, ColourChooser, Wrap, WrapItem, FormControl, CodeTextarea, SearchableSelect, Card } from '@/components/lib'
import ConfirmButton from '../includes/ConfirmButton'
import Integer from '../fields/Integer'
import utils from '../../utils/utils'
import api from '../../api'
import useStore from '../../store'

export const categories = [
  {
    name: 'Essential',
    description: 'Basic data inputs, including text, numeric, sliders, and multiple choice.',
    icon: FaTasks,
    feature: 'basicDataTypes',
    types: [
      { key: 'text', mobile: true, label: 'Short text', description: 'For collecting a short piece of text (e.g. a word, phrase or a sentence).' },
      { key: 'paragraph', mobile: true, label: 'Long text', description: 'For collecting a paragraph or more of text.' },
      { key: 'choice', mobile: true, label: 'Choice', description: 'Allow participants to select one or more options from a selection.' },
      { key: 'integer', mobile: true, label: 'Integer', description: 'For collecting a single integer value (e.g. 42).' },
      { key: 'float', mobile: true, label: 'Decimal number', description: 'For collecting a single floating point number (e.g. 276.42). We refer to this internally as a "float".' },
      { key: 'slider', mobile: true, label: 'Slider', description: 'For collecting a single integer value on a range between a specified minimum and maximum (e.g. between 1 and 10).' },
      { key: 'date', mobile: true, label: 'Date', description: 'For collecting a point in time represented by a date (e.g. a date of birth). You do not need to record the date of submission since Trialflare will handle this for you.' },
      { key: 'datetime', mobile: true, label: 'Date & time', description: 'For collecting a point in time represented by a date and time (e.g. the time the participant used a product). You do not need to record the date of submission since Trialflare will handle this for you.' },
      { key: 'time', mobile: true, label: 'Time', description: 'For collecting a time of day only (e.g. wake-up time, medication time). No date is stored.' }
    ]
  },
  {
    name: 'Advanced',
    description: 'More complex data types.',
    icon: FaStar,
    types: [
      { key: 'temperature', mobile: true, label: 'Temperature', description: 'For collecting a temperature measurement.', feature: 'basicDataTypes' },
      { key: 'weight', mobile: true, label: 'Weight', description: 'For collecting a weight measurement.', feature: 'basicDataTypes' },
      { key: 'height', mobile: true, label: 'Height', description: 'For collecting a height measurement.', feature: 'basicDataTypes' },
      { key: 'bmi', mobile: true, label: 'BMI', description: 'For collecting a person\'s height and weight and calculating their body-mass index.', feature: 'basicDataTypes' },
      { key: 'file', mobile: false, label: 'File', description: 'For allowing the upload and collection of files, such as PDFs, spreadsheets, documents, and more.', feature: 'fileDataType' },
      { key: 'image', mobile: true, label: 'Image', description: 'For allowing the upload and collection of image files. On mobile, this allows the participant to use the camera.', feature: 'imageDataType' },
      { key: 'video', mobile: true, label: 'Video', description: 'For allowing the upload and collection of video files.', feature: 'videoDataType' },
      { key: 'food', mobile: true, label: 'Nutrition input', description: 'Enter a food or drink item or scan a barcode.', feature: 'foodDataType' },
      { key: 'calculation', mobile: true, label: 'Calculation', description: 'A computed value from a formula.', feature: 'calculationDataType' },
      { key: 'teethChart', mobile: false, label: 'Teeth chart', description: 'For collecting dental chart annotations including conditions, surfaces, and clinical notes for each tooth.', feature: 'teethChart' }
    ]
  },
  {
    name: 'Cognition',
    description: 'A suite of tests for measuring various cognitive capabilities.',
    icon: FaBrain,
    feature: 'cognitionDataTypes',
    types: [
      { key: 'stroop', mobile: true, label: 'Stroop', description: 'For running and collecting data from a Stroop test.' },
      { key: 'pal', mobile: true, label: 'PAL', description: 'For running and collecting data from a paired associates learning test.' },
      { key: 'ravlt', mobile: false, label: 'RAVLT', description: 'For running and collecting data from a Rey Auditory Verbal Learning Test.' }
    ]
  },
  {
    name: 'PROMIS',
    description: 'NIH PROMIS validated patient-reported outcome questionnaires.',
    icon: FaClipboardList,
    feature: 'promis',
    types: [
      { key: 'promisForm', mobile: false, label: 'PROMIS Form', description: 'A validated NIH PROMIS questionnaire administered via the Assessment Center.' },
      { key: 'promisBattery', mobile: false, label: 'PROMIS Battery', description: 'A collection of PROMIS forms administered sequentially as a single battery.' }
    ]
  }
]

export const times = [
  { key: '10', value: 10, text: '10 seconds' },
  { key: '30', value: 30, text: '30 seconds' },
  { key: '45', value: 45, text: '45 seconds' },
  { key: '60', value: 60, text: '1 minute' },
  { key: '120', value: 120, text: '2 minutes' }
]

export const nutritionTypes = [
  { key: 'carbohydrate', name: 'Carbohydrate', unit: 'g' },
  { key: 'fat', name: 'Fat', unit: 'g' },
  { key: 'saturatedFat', name: 'Saturated fat', unit: 'g' },
  { key: 'fibre', name: 'Fibre', unit: 'g' },
  { key: 'protein', name: 'Protein', unit: 'g' },
  { key: 'salt', name: 'Salt', unit: 'g' },
  { key: 'sugars', name: 'Sugars', unit: 'g' },
  { key: 'energyKcal', name: 'Energy (Kcal)', unit: 'Kcal' }
]

function NewData ({ trigger, trial, dataTypeId, onComplete }) {
  const [open, setOpen] = useState(false)
  const [dataType, setDataType] = useState()
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState()
  const [error, setError] = useState()
  const [saving, setSaving] = useState(false)
  const [selectedType, setSelectedType] = useState('text')
  const [name, setName] = useState('')
  const [nickname, setNickname] = useState('')
  const [isRestricted, setIsRestricted] = useState(false)
  const [isPii, setIsPii] = useState(false)
  const [textMin, setTextMin] = useState(0)
  const [textMax, setTextMax] = useState(0)
  const [minValue, setMinValue] = useState(0)
  const [maxValue, setMaxValue] = useState(0)
  const [sliderMin, setSliderMin] = useState(1)
  const [sliderMax, setSliderMax] = useState(10)
  const [dateMin, setDateMin] = useState()
  const [dateMax, setDateMax] = useState()
  const [timeMin, setTimeMin] = useState()
  const [timeMax, setTimeMax] = useState()
  const [choiceOptions, setChoiceOptions] = useState([])
  const [newChoiceOption, setNewChoiceOption] = useState('')
  const [choiceMultipleSelection, setChoiceMultipleSelection] = useState(false)
  const [choiceIncludeNone, setChoiceIncludeNone] = useState(false)
  const [choiceShowAsDropdown, setChoiceShowAsDropdown] = useState(false)
  const [stroopOptions, setStroopOptions] = useState([])
  const [newStroopOption, setNewStroopOption] = useState('')
  const [newStroopColour, setNewStroopColour] = useState('')
  const [stroopTimeLimit, setStroopTimeLimit] = useState(60)
  const [palWordPairs, setPalWordPairs] = useState([])
  const [palPairTimeout, setPalPairTimeout] = useState(3)
  const [palAnswerTimeout, setPalAnswerTimeout] = useState(180)
  const [palDisplayAnswerTimer, setPalDisplayAnswerTimer] = useState(true)
  const [palDisplayCorrectResult, setPalDisplayCorrectResult] = useState(true)
  const [ravltTestSet, setRavltTestSet] = useState(Array(15).fill(''))
  const [ravltInterruptionSet, setRavltInterruptionSet] = useState(Array(15).fill(''))
  const [ravltPreInterruptionTests, setRavltPreInterruptionTests] = useState(3)
  const [ravltPostInterruptionTests, setRavltPostInterruptionTests] = useState(1)
  const [foodSource, setFoodSource] = useState('openFoodFacts')
  const [requiredFoodFields, setRequiredFoodFields] = useState(['protein', 'carbohydrate', 'fat', 'energyKcal'])
  const [formula, setFormula] = useState('')
  const [formulaDescription, setFormulaDescription] = useState('')
  const [generatingDescription, setGeneratingDescription] = useState(false)
  const [selectedDataTypeForInsert, setSelectedDataTypeForInsert] = useState('')
  const [promisFormOid, setPromisFormOid] = useState('')
  const [promisShowTScore, setPromisShowTScore] = useState(false)
  const [promisQuery, setPromisQuery] = useState('')
  const [promisForms, setPromisForms] = useState([])
  const [promisFormsLoading, setPromisFormsLoading] = useState(false)
  const [promisBatteryOid, setPromisBatteryOid] = useState('')
  const [promisBatteryQuery, setPromisBatteryQuery] = useState('')
  const [promisBatteries, setPromisBatteries] = useState([])
  const [promisBatteriesLoading, setPromisBatteriesLoading] = useState(false)
  const { team, updateTrial, addDataType, dataTypes } = useStore()
  const isEditing = !!dataTypeId

  useEffect(() => {
    if (!dataTypeId || !open) return
    setDataType()
    setLoadError()
    setLoading(true)
    api.dataTypes.get(dataTypeId, dataType => {
      setDataType(dataType)
      setLoading(false)
    }, err => {
      setLoading(false)
      setLoadError(err.message)
    })
  }, [dataTypeId, open])

  useEffect(() => {
    if (open && utils.hasFeature(team, 'promis') && !promisForms.length) {
      setPromisFormsLoading(true)
      api.promis.listForms(({ forms }) => {
        setPromisForms(forms || [])
        setPromisFormsLoading(false)
      }, () => setPromisFormsLoading(false))
    }
    if (open && utils.hasFeature(team, 'promis') && !promisBatteries.length) {
      setPromisBatteriesLoading(true)
      api.promis.listBatteries(({ batteries }) => {
        setPromisBatteries(batteries || [])
        setPromisBatteriesLoading(false)
      }, () => setPromisBatteriesLoading(false))
    }
  }, [open])

  useEffect(() => {
    if (dataType?.name) {
      setName(dataType.name)
      setNickname(dataType.nickname)
      setIsRestricted(dataType.isRestricted || false)
      setIsPii(dataType.isPii || false)
      setSelectedType(dataType.type)
      setTextMin(dataType.minimumLength || 0)
      setTextMax(dataType.maximumLength || 0)
      setMinValue(dataType.minimumValue || 0)
      setMaxValue(dataType.maximumValue || 0)
      setSliderMin(dataType.minimum ?? 1)
      setSliderMax(dataType.maximum ?? 10)
      setChoiceOptions(dataType.options || [])
      setChoiceMultipleSelection(dataType.multiple || false)
      setChoiceIncludeNone(dataType.includeNone || false)
      setChoiceShowAsDropdown(dataType.showAsDropdown || false)
      setDateMin(dataType.earliestDate ? new Date(dataType.earliestDate) : null)
      setDateMax(dataType.latestDate ? new Date(dataType.latestDate) : null)
      setTimeMin(dataType.earliestTime || null)
      setTimeMax(dataType.latestTime || null)
      setStroopOptions(dataType.options || [])
      setStroopTimeLimit(dataType.timeLimit || 60)
      setPalWordPairs(dataType.wordPairs || [])
      setPalPairTimeout(dataType.pairTimeout || 3)
      setPalAnswerTimeout(dataType.answerTimeout ?? 180)
      setPalDisplayAnswerTimer(dataType.displayAnswerTimer)
      setPalDisplayCorrectResult(dataType.displayCorrectResult)
      setRavltTestSet(dataType.testSet || Array(15).fill(''))
      setRavltInterruptionSet(dataType.interruptionSet || Array(15).fill(''))
      setRavltPreInterruptionTests(dataType.preInterruptionTests || 3)
      setRavltPostInterruptionTests(dataType.postInterruptionTests || 1)
      setFoodSource(dataType.foodSource || 'openFoodFacts')
      setRequiredFoodFields(dataType.requiredFoodFields || ['protein', 'carbohydrate', 'fat', 'energyKcal'])
      setFormula(dataType.formula || '')
      setFormulaDescription(dataType.formulaDescription || '')
      setPromisFormOid(dataType.promisFormOid || '')
      setPromisBatteryOid(dataType.promisBatteryOid || '')
      setPromisShowTScore(dataType.promisShowTScore || false)
    }
  }, [dataType])

  function complete (dataType) {
    if (!dataTypeId) {
      setName('')
      setNickname('')
      setIsRestricted(false)
      setIsPii(false)
      setTextMin(0)
      setTextMax(0)
      setMinValue(0)
      setMaxValue(0)
      setSliderMin(1)
      setSliderMax(10)
      setChoiceOptions([])
      setChoiceMultipleSelection(false)
      setChoiceIncludeNone(false)
      setChoiceShowAsDropdown(false)
      setDateMin(null)
      setDateMax(null)
      setTimeMin(null)
      setTimeMax(null)
      setStroopOptions([])
      setStroopTimeLimit(60)
      setPalWordPairs([])
      setPalPairTimeout(3)
      setPalAnswerTimeout(180)
      setPalDisplayAnswerTimer(true)
      setPalDisplayCorrectResult(true)
      setRavltTestSet(Array(15).fill(''))
      setRavltInterruptionSet(Array(15).fill(''))
      setRavltPreInterruptionTests(3)
      setRavltPostInterruptionTests(1)
      setFormula('')
      setFormulaDescription('')
      setPromisFormOid('')
      setPromisBatteryOid('')
      setPromisShowTScore(false)
      setPromisQuery('')
      setPromisBatteryQuery('')
    }
    setOpen(false)
    if (onComplete) onComplete(dataType)
  }

  function create (customCallback) {
    setError()
    if (isEditing && !dataType?._id) {
      return setError(loading
        ? 'This data type is still loading'
        : 'We could not load this data type')
    }
    const data = { name, nickname, isRestricted, isPii, type: selectedType }
    if (['text', 'paragraph'].indexOf(selectedType) > -1) {
      data.minimumLength = textMin || 0
      data.maximumLength = textMax || 0
      if (textMin < 0 || textMax < 0) return setError('Minimum and maximum values must be above 0')
      if (textMin && textMax && textMax < textMin) return setError('Maximum text length cannot be lower than the minimum')
    }
    if (['integer', 'float'].indexOf(selectedType) > -1) {
      data.minimumValue = minValue || 0
      data.maximumValue = maxValue || 0
      if (minValue && maxValue && maxValue < minValue) return setError('Maximum value cannot be lower than the minimum')
    }
    if (selectedType === 'slider') {
      data.minimum = sliderMin
      data.maximum = sliderMax
      if (data.minimum == null || data.maximum == null) return setError('You must choose a minimum and maximum slider value')
    }
    if (selectedType === 'choice') {
      data.options = choiceOptions
      data.multiple = choiceMultipleSelection
      data.includeNone = choiceIncludeNone
      data.showAsDropdown = !choiceMultipleSelection && choiceShowAsDropdown
      if (!data.options || !data.options.length) return setError('You must provide at least one option')
    }
    if (['date', 'datetime'].indexOf(selectedType) > -1) {
      data.earliestDate = dateMin || null
      data.latestDate = dateMax || null
      if (dateMin && dateMax && dateMax < dateMin) return setError('Latest date cannot be earlier than the earliest date')
    }
    if (selectedType === 'time') {
      data.earliestTime = timeMin || null
      data.latestTime = timeMax || null
      if (timeMin && timeMax && timeMax < timeMin) return setError('Latest time cannot be earlier than the earliest time')
    }
    if (selectedType === 'stroop') {
      data.options = stroopOptions
      data.timeLimit = stroopTimeLimit
      if (!data.options || !data.options.length) return setError('You must provide at least one colour test')
    }
    if (selectedType === 'pal') {
      data.wordPairs = palWordPairs
      data.pairTimeout = palPairTimeout
      data.answerTimeout = palAnswerTimeout || 0
      data.displayAnswerTimer = palDisplayAnswerTimer
      data.displayCorrectResult = palDisplayCorrectResult
      if (!data.wordPairs || !data.wordPairs.length) return setError('You must provide at least one word pair')
    }
    if (selectedType === 'ravlt') {
      data.testSet = ravltTestSet
      data.interruptionSet = ravltInterruptionSet
      data.preInterruptionTests = ravltPreInterruptionTests
      data.postInterruptionTests = ravltPostInterruptionTests
    }
    if (selectedType === 'food') {
      data.foodSource = foodSource
      data.requiredFoodFields = requiredFoodFields
    }
    if (selectedType === 'calculation') {
      data.formula = formula?.trim() || null
    }
    if (selectedType === 'promisForm') {
      if (!promisFormOid) return setError('Please select a PROMIS form')
      data.promisFormOid = promisFormOid
      data.promisShowTScore = promisShowTScore
    }
    if (selectedType === 'promisBattery') {
      if (!promisBatteryOid) return setError('Please select a PROMIS battery')
      data.promisBatteryOid = promisBatteryOid
      data.promisShowTScore = promisShowTScore
    }
    setSaving(true)
    if (isEditing) {
      api.dataTypes.update(dataType._id, data, dataType => {
        utils.toast('success', 'Data type saved')
        addDataType(dataType)
        customCallback ? customCallback(dataType) : complete(dataType)
        setSaving(false)
      }, err => { setSaving(false); setError(err.message) })
    } else {
      api.trials.createDataType(trial._id, data, dataType => {
        addDataType(dataType)
        updateTrial(trial._id, { dataTypeCount: (trial.dataTypeCount ?? 1) + 1 })
        utils.toast('success', 'Data type created')
        customCallback ? customCallback(dataType) : complete(dataType)
        setSaving(false)
      }, err => { setSaving(false); setError(err.message) })
    }
  }

  function addStroopOption () {
    if (!newStroopColour || !newStroopOption) return
    stroopOptions.push({
      colour: newStroopColour,
      text: newStroopOption
    })
    setStroopOptions(stroopOptions)
    setNewStroopOption('')
    setNewStroopColour('')
  }
  function removeStroopOption (index) {
    const options = Object.assign([], stroopOptions)
    options.splice(index, 1)
    setStroopOptions(options)
  }

  function addPalWordPair () {
    setPalWordPairs([...palWordPairs, {
      words: ['', ''],
      related: false
    }])
  }
  function removePalWordPair (index) {
    const newPairs = palWordPairs.filter((pair, i) => i !== index)
    setPalWordPairs(newPairs)
  }
  function updatePalWordPairRelated (index, related) {
    const newPairs = palWordPairs.map((pair, i) => {
      if (i === index) {
        pair.related = related
      }
      return pair
    })
    setPalWordPairs(newPairs)
  }
  function updatePalWordPair (index, pairIndex, newWord) {
    const newPairs = palWordPairs.map((pair, i) => {
      if (i === index) {
        pair.words[pairIndex] = newWord
      }
      return pair
    })
    setPalWordPairs(newPairs)
  }

  function addChoiceOption () {
    if (!newChoiceOption) return
    choiceOptions.push(newChoiceOption.trim())
    setChoiceOptions(choiceOptions)
    setNewChoiceOption('')
  }
  function removeChoiceOption (i) {
    const options = Object.assign([], choiceOptions)
    options.splice(i, 1)
    setChoiceOptions(options)
  }

  return (
    <>
      {trigger
        ? React.cloneElement(trigger, { onClick: () => setOpen(true) })
        : <Button colorPalette='primary' onClick={() => setOpen(true)}>Create new data type</Button>}
      <SimpleModal
        isOpen={open} onClose={() => setOpen(false)} size='lg'
        header={isEditing ? 'Edit your data type' : 'Create a new data type'}
        body={
          <Stack gap={5}>
            {loadError &&
              <Alert status='error' variant='left-accent' title='There was a problem loading your data type'>
                {loadError}. Please close this window and try again.
              </Alert>}
            <Card gap={5}>
              <Stack>
                <Heading as='h3' size='md'>1. About your data type</Heading>

                <FormControl label='Name' helperText={'This helps you identify the data type. It\'s not shown to participants.'}>
                  <Input placeholder='My data type' type='text' value={name} onChange={e => setName(e.target.value)} />
                </FormControl>
              </Stack>

              <FormControl label='Nickname' helperText='Optionally, provide a more human-friendly nickname for this data type.'>
                <Input placeholder='My nickname' type='text' value={nickname} onChange={e => setNickname(e.target.value)} />
              </FormControl>

              <FormControl helperText='Study data recorded for restricted types is only available to users with the appropriate permissions.'>
                <Checkbox isChecked={isRestricted || false} onChange={e => setIsRestricted(e.checked)}>Restrict this data type</Checkbox>
              </FormControl>

              <FormControl label='PII data type' helperText='Mark this data type as containing personally identifiable information. Only users with the PII study data permission can view responses for this field.'>
                <Checkbox isChecked={isPii || false} onChange={e => setIsPii(e.checked)}>This data type contains PII</Checkbox>
              </FormControl>
            </Card>

            <Card gap={3}>
              <Box display='flex' justifyContent='space-between'>
                <Heading as='h3' size='md'>2. Choose a format</Heading>
                {selectedType && <Badge colorPalette='blue' size='lg'>{selectedType}</Badge>}
              </Box>
              <Text>Select a measurement from the data type library.</Text>
              <SimpleAccordion
                items={[
                  ...categories
                    .filter(c => c.feature !== 'promis')
                    .map(c => ({
                      key: c.name,
                      label: (
                        <Box flex='1' textAlign='left'>
                          <Heading as='h4' size='sm' color='primary.500'><Box as={c.icon} mr={2} display='inline-block' /> {c.name}</Heading>
                          <Text>{c.description}</Text>
                        </Box>),
                      content: (
                        <RadioGroup value={selectedType} colorPalette='primary'>
                          <Grid templateColumns={{ md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={2}>
                            {c.types.map(t => {
                              const allowed = utils.hasFeature(team, t.feature || c.feature)
                              return (
                                <Stack key={t.key} p={3} borderWidth='1px' bg={selectedType === t.key ? 'primary.muted' : 'bg.surface'} onClick={(e, r) => setSelectedType(t.key)} cursor='pointer'>
                                  {!allowed && <Box><Badge colorPalette='orange' size='sm'><FaLock /> Not available in your team</Badge></Box>}
                                  <Radio value={t.key} disabled={!allowed} name='type' checked={selectedType === t.key}>
                                    <Heading as='h4' size='sm' fontWeight='bold'>{t.label}</Heading>
                                  </Radio>
                                  <Wrap gap={1}>
                                    <Badge colorPalette='gray' size='sm'>{t.key}</Badge>
                                    {t.mobile && <Badge colorPalette='green' size='sm'>Participant compatible</Badge>}
                                  </Wrap>
                                  <Text fontSize='xs'>{t.description}</Text>
                                </Stack>
                              )
                            })}
                          </Grid>
                        </RadioGroup>)
                    })),
                  ...(utils.hasFeature(team, 'promis')
                    ? [{
                        key: 'PROMIS',
                        label: (
                          <Box flex='1' textAlign='left'>
                            <Heading as='h4' size='sm' color='primary.500'><Box as={FaClipboardList} mr={2} display='inline-block' /> PROMIS Forms</Heading>
                            <Text>NIH PROMIS validated patient-reported outcome questionnaires.</Text>
                          </Box>),
                        content: (
                          <PromisFormPicker
                            promisForms={promisForms}
                            promisFormsLoading={promisFormsLoading}
                            promisFormOid={promisFormOid}
                            setPromisFormOid={setPromisFormOid}
                            promisQuery={promisQuery}
                            setPromisQuery={setPromisQuery}
                            setSelectedType={setSelectedType}
                          />
                        )
                      }, {
                        key: 'PROMIS_BATTERY',
                        label: (
                          <Box flex='1' textAlign='left'>
                            <Heading as='h4' size='sm' color='primary.500'><Box as={FaClipboardList} mr={2} display='inline-block' /> PROMIS Batteries</Heading>
                            <Text>Collections of PROMIS forms administered together as a single battery.</Text>
                          </Box>),
                        content: (
                          <PromisBatteryPicker
                            promisBatteries={promisBatteries}
                            promisBatteriesLoading={promisBatteriesLoading}
                            promisBatteryOid={promisBatteryOid}
                            setPromisBatteryOid={setPromisBatteryOid}
                            promisBatteryQuery={promisBatteryQuery}
                            setPromisBatteryQuery={setPromisBatteryQuery}
                            setSelectedType={setSelectedType}
                          />
                        )
                      }]
                    : [])
                ]}
              />
            </Card>

            {['text', 'paragraph'].indexOf(selectedType) > -1 &&
              <Card gap={3}>
                <Heading as='h3' size='md'>3. Configure this data type</Heading>
                <Text>Choose extra settings for this field.</Text>
                <Text>Minimum allowed text length <small>(Leave as 0 or blank for no minimum)</small></Text>
                <Input placeholder='1' type='number' value={textMin} onChange={e => setTextMin(parseInt(e.target.value))} />
                <Text>Maximum allowed text length <small>(Leave as 0 or blank for no maximum)</small></Text>
                <Input placeholder='1' type='number' value={textMax} onChange={e => setTextMax(parseInt(e.target.value))} />
              </Card>}

            {['integer', 'float'].indexOf(selectedType) > -1 &&
              <Card gap={3}>
                <Heading as='h3' size='md'>3. Configure this data type</Heading>
                <Text>Choose extra settings for this field.</Text>
                <Text>Minimum allowed value <small>(Leave as 0 or blank for no minimum)</small></Text>
                <Integer value={minValue} onChange={setMinValue} />
                <Text>Maximum allowed value <small>(Leave as 0 or blank for no maximum)</small></Text>
                <Integer value={maxValue} onChange={setMaxValue} />
              </Card>}

            {selectedType === 'slider' &&
              <Card gap={3}>
                <Heading as='h3' size='md'>3. Slider settings</Heading>
                <Text>Allow participants to select a number between the following minimum and maximum.</Text>
                <Text>Minimum value</Text>
                <Integer value={sliderMin} onChange={setSliderMin} />
                <Text>Maximum value</Text>
                <Integer value={sliderMax} onChange={setSliderMax} />
              </Card>}

            {selectedType === 'choice' &&
              <Card gap={3}>
                <Heading as='h4' size='md'>3. Choice settings</Heading>
                <Text>Manage the options for participants to select from.</Text>

                <Text>New option</Text>
                <Stack direction='row'>
                  <Input value={newChoiceOption} onChange={e => setNewChoiceOption(e.target.value)} />
                  <Button onClick={addChoiceOption}>Add option</Button>
                </Stack>
                <Wrap gap={2}>
                  {choiceOptions && choiceOptions.map((c, i) =>
                    <WrapItem key={c}>
                      <Tag
                        size='lg' key={c} variant='subtle' colorPalette='cyan' mr={2}
                        label={c}
                        onClose={() => removeChoiceOption(i)}
                      />
                    </WrapItem>
                  )}
                </Wrap>
                <Box>
                  <Checkbox isChecked={choiceMultipleSelection} onChange={e => setChoiceMultipleSelection(e.checked)}>Allow participants to select more than one option</Checkbox>
                </Box>
                {!choiceMultipleSelection &&
                  <Box>
                    <Checkbox isChecked={choiceShowAsDropdown} onChange={e => setChoiceShowAsDropdown(e.checked)}>Show as dropdown <small>(not available on mobile)</small></Checkbox>
                  </Box>}
                <Box>
                  <Checkbox isChecked={choiceIncludeNone} onChange={e => setChoiceIncludeNone(e.checked)}>Include an additional "None" option <small>(not available on mobile)</small></Checkbox>
                </Box>
              </Card>}

            {['date', 'datetime'].indexOf(selectedType) > -1 &&
              <Card gap={3}>
                <Heading as='h3' size='md'>3. Date settings</Heading>
                <Text>Define the earliest and latest date available for this data type. Leave blank to leave the field as unrestricted.</Text>
                <Text>Earliest available date</Text>
                <Box maxW='300px' display='flex'>
                  <DateChooser value={dateMin} onChange={setDateMin} />
                  <Button ml={5} onClick={() => setDateMin(null)}>Unset</Button>
                </Box>

                <Text>Latest available date</Text>
                <Box maxW='300px' display='flex'>
                  <DateChooser value={dateMax} onChange={setDateMax} />
                  <Button ml={5} onClick={() => setDateMax(null)}>Unset</Button>
                </Box>
              </Card>}

            {selectedType === 'time' &&
              <Card gap={3}>
                <Heading as='h3' size='md'>3. Time settings</Heading>
                <Text>Define the earliest and latest time available for this data type. Leave blank to leave the field as unrestricted.</Text>
                <Text>Earliest available time</Text>
                <Box maxW='300px' display='flex'>
                  <TimeChooser value={timeMin} onChange={setTimeMin} />
                  <Button ml={5} onClick={() => setTimeMin(null)}>Unset</Button>
                </Box>

                <Text>Latest available time</Text>
                <Box maxW='300px' display='flex'>
                  <TimeChooser value={timeMax} onChange={setTimeMax} />
                  <Button ml={5} onClick={() => setTimeMax(null)}>Unset</Button>
                </Box>
              </Card>}

            {selectedType === 'stroop' &&
              <Card gap={3}>
                <Heading as='h3' size='md'>3. Configure this Stroop test</Heading>
                <Text>In the Stroop test, participants will be shown a series of "colour tests" (the name of a colour written in another colour), and will be asked to select the name of the colour the colour test is written in.</Text>
                <Text>Add text and colour combinations below for the Stroop test. We recommend choosing colours that are clearly legible on a white background.</Text>

                <Stack direction='row' alignItems='end'>
                  <Box>
                    <Text>Text colour</Text>
                    <ColourChooser value={newStroopColour} onChange={setNewStroopColour} />
                  </Box>
                  <Box>
                    <Text>Colour name</Text>
                    <Input value={newStroopOption} onChange={e => setNewStroopOption(e.target.value)} placeholder='RED' />
                  </Box>
                  <Button onClick={addStroopOption}>Add option</Button>
                </Stack>

                <Wrap>
                  {stroopOptions && stroopOptions.length > 0 &&
                    <>
                      {stroopOptions.map((o, i) =>
                        <WrapItem key={o.text}>
                          <Tag
                            size='lg' variant='subtle' mr={2}
                            label={o.text}
                            onClose={() => removeStroopOption(i)}
                          />
                        </WrapItem>
                      )}
                    </>}
                </Wrap>
                <Stack>
                  <Text>How long should the test keep generating new combinations for?</Text>
                  <Select value={stroopTimeLimit} onChange={e => setStroopTimeLimit(e.target.value)}>
                    {times.map(o => <option key={o.key} value={o.value}>{o.text}</option>)}
                  </Select>
                </Stack>
              </Card>}

            {selectedType === 'pal' &&
              <Card gap={3}>
                <Heading as='h3' size='md'>3. Configure this PAL test</Heading>
                <Text>In the PAL test, participants will be shown a series of word pair combinations, and are then asked to remember the correct pair for each given word.</Text>
                <Text>Add word pairs below for the PAL test.</Text>

                <Box display={{ md: 'flex' }}>
                  {palWordPairs.length > 0 &&
                    <SimpleTable
                      headers={['Left word', 'Right word', 'Related', '']} rows={palWordPairs.map((p, i) => [
                        <Input key={`${i}-0`} value={p.words[0]} onChange={e => updatePalWordPair(i, 0, e.target.value)} />,
                        <Input key={`${i}-1`} value={p.words[1]} onChange={e => updatePalWordPair(i, 1, e.target.value)} />,
                        <Checkbox key={`${i}-r`} isChecked={p.related} onChange={e => updatePalWordPairRelated(i, e.checked)} />,
                        <Button key={`${i}-d`} variant='ghost' onClick={e => removePalWordPair(i)}><Icon><FaTrash /></Icon></Button>
                      ])}
                    />}
                </Box>
                <Button onClick={addPalWordPair}>Add word pair</Button>

                <Box>
                  <Text>For how many seconds should participants be shown each pair?</Text>
                  <Input type='number' value={palPairTimeout} onChange={e => setPalPairTimeout(parseInt(e.target.value) || '')} w='100px' />
                  <Text>For how many seconds should participants be allowed to select matches for? (Leave 0 or blank for no limit)</Text>
                  <Input type='number' placeholder='0' value={palAnswerTimeout} onChange={e => setPalAnswerTimeout(parseInt(e.target.value) || '')} w='100px' />
                  <Text>Should the participant see a timer counting down when selecting pairs?</Text>
                  <Checkbox isChecked={palDisplayAnswerTimer} onChange={e => setPalDisplayAnswerTimer(e.checked)}>Display answer timer</Checkbox>
                  <Text>Should the participant see if they selected the correct pair after selecting?</Text>
                  <Checkbox isChecked={palDisplayCorrectResult} onChange={e => setPalDisplayCorrectResult(e.checked)}>Display correct result</Checkbox>
                </Box>
              </Card>}

            {selectedType === 'ravlt' &&
              <Card gap={3}>
                <Heading as='h3' size='md'>3. Configure this RAVLT test</Heading>
                <Text>In the Rey Auditory Verbal Learning Test (RAVLT), participants will be shown or told a series of words and asked to recall them after a delay.</Text>
                <Alert status='info' variant='left-accent'>Please note that this test must be conducted by an administrator who fills-in the results on the participant page.</Alert>
                <Text>Provide two sets of 15 words. The first set is the "test" set and the second set is the "interruption" set.</Text>

                <Stack direction='row' spacing={5}>
                  <Box>
                    <Text>Number of pre-interruption tests</Text>
                    <Select value={ravltPreInterruptionTests} onChange={e => setRavltPreInterruptionTests(e.target.value)}>
                      {[1, 2, 3, 4, 5].map(o => <option key={o} value={o}>{o}</option>)}
                    </Select>
                  </Box>
                  <Box>
                    <Text>Number of post-interruption tests</Text>
                    <Select value={ravltPostInterruptionTests} onChange={e => setRavltPostInterruptionTests(e.target.value)}>
                      {[1, 2, 3, 4, 5].map(o => <option key={o} value={o}>{o}</option>)}
                    </Select>
                  </Box>
                </Stack>

                <SimpleTable
                  headers={['Test set', 'Interruption set']} rows={ravltTestSet.map((w, i) => [
                    <Input key={`${i}-test`} placeholder={`Test word ${i + 1}`} value={ravltTestSet[i]} onChange={e => setRavltTestSet(ravltTestSet.map((w, j) => i === j ? e.target.value : w))} />,
                    <Input key={`${i}-interrupt`} placeholder={`Interruption word ${i + 1}`} value={ravltInterruptionSet[i]} onChange={e => setRavltInterruptionSet(ravltInterruptionSet.map((w, j) => i === j ? e.target.value : w))} />
                  ])}
                />
              </Card>}

            {selectedType === 'food' &&
              <Card gap={3}>
                <Heading as='h3' size='md'>3. Configure the food input type</Heading>

                <Heading size='sm'>Nutrition data source</Heading>
                <Text>Trialflare supports different food nutrition sources for this type of data.</Text>
                <Select placeholder='Choose a food nutrition data source...' value={foodSource} onChange={e => setFoodSource(e.target.value)}>
                  <option value='openFoodFacts'>Open Food Facts</option>
                  <option value='nutritics'>Nutritics</option>
                </Select>

                <Heading size='sm'>Nutritional information required</Heading>
                <Text>Trialflare will try to automatically determine nutritional values from the data source. However, if these are unavailable for a particular item, you can specify the fields required to be completed manually.</Text>
                {nutritionTypes.map(n =>
                  <Box key={n.key} p={2} display='inline-block'>
                    <Checkbox
                      key={n.key} isChecked={requiredFoodFields.indexOf(n.key) > -1}
                      onChange={e => {
                        const index = requiredFoodFields.indexOf(n.key)
                        const newRequiredFoodFields = Object.assign([], requiredFoodFields)
                        if (index === -1) newRequiredFoodFields.push(n.key)
                        else newRequiredFoodFields.splice(index, 1)
                        setRequiredFoodFields(newRequiredFoodFields)
                      }}
                    >
                      {n.name}
                    </Checkbox>
                  </Box>
                )}
              </Card>}

            {selectedType === 'promisForm' && promisFormOid &&
              <Stack gap={3} rounded='md' p={5} borderWidth='2px' bg='bg.surface'>
                <Heading as='h3' size='md'>3. PROMIS form settings</Heading>
                <Text>Configure how the assessment is presented to participants.</Text>
                <Checkbox isChecked={promisShowTScore} onChange={e => setPromisShowTScore(e.checked)}>
                  Show T-Score to participant on completion
                </Checkbox>
              </Stack>}

            {selectedType === 'promisBattery' && promisBatteryOid &&
              <Stack gap={3} rounded='md' p={5} borderWidth='2px' bg='bg.surface'>
                <Heading as='h3' size='md'>3. PROMIS battery settings</Heading>
                <Text>Configure how the battery is presented to participants. Each form in the battery is administered in turn.</Text>
                {isEditing && dataType?.promisProprEligible != null &&
                  <Alert status={dataType.promisProprEligible ? 'success' : 'info'} fontSize='sm'>
                    {dataType.promisProprEligible
                      ? 'This battery contains all seven PROPr domains, so a combined PROPr score will be calculated on completion.'
                      : 'This battery does not contain all seven PROPr domains, so no combined PROPr score is calculated. Individual form scores are still recorded.'}
                  </Alert>}
                <Checkbox isChecked={promisShowTScore} onChange={e => setPromisShowTScore(e.checked)}>
                  Show T-Scores to participant on completion
                </Checkbox>
              </Stack>}

            {selectedType === 'calculation' &&
              <Card gap={3}>
                <Heading as='h3' size='md'>3. Calculation formula</Heading>
                <Text mb={3}>Write your formula using basic Python syntax. Use <code>{'{{DataTypeName}}'}</code> for values from other named data types.</Text>
                <FormControl label='Formula'>
                  <CodeTextarea
                    value={formula ?? ''}
                    onChange={e => setFormula(e.target.value)}
                    placeholder='e.g. {{Weight}} / (({{Height}}/100)**2)'
                  />
                </FormControl>

                <Stack direction='row' alignItems='center' gap={3}>
                  {dataTypes?.length > 0 &&
                    <SearchableSelect
                      options={dataTypes
                        .filter(d => d.type !== 'calculation')
                        .map(d => ({
                          value: d._id,
                          label: `${d.name} (${d.type})`
                        }))}
                      value={selectedDataTypeForInsert}
                      onChange={(value) => {
                        const selectedDataType = dataTypes.find(d => d._id === value)
                        if (selectedDataType) {
                          setFormula((formula ?? '') + '{{' + selectedDataType.name + '}}')
                          // Clear selection so it's ready for another insertion
                          setSelectedDataTypeForInsert('')
                        }
                      }}
                      placeholder='Insert a data type...'
                      size='sm'
                      maxW='400px'
                      emptyMessage='No data types found'
                    />}
                  {(formula && formula.length > 10) &&
                    <ConfirmButton
                      trigger={
                        <Button
                          size='xs'
                          variant='outline'
                          isLoading={generatingDescription}
                          disabled={generatingDescription}
                        >
                          <Icon as={LuSparkles} />
                          Describe and check my formula
                        </Button>
                      }
                      header='Save data type first?'
                      body='You need to save the data type before we can generate a description. Would you like to save it now?'
                      onConfirm={() => {
                        // Save first, then generate description
                        create((savedDataType) => {
                          if (selectedType === 'calculation' && savedDataType.formula) {
                            setGeneratingDescription(true)
                            api.dataTypes.generateDescription(savedDataType._id, updatedDataType => {
                              setFormulaDescription(updatedDataType.formulaDescription || '')
                              setGeneratingDescription(false)
                              addDataType(updatedDataType)
                              utils.toast('success', 'Formula description generated')
                            }, err => {
                              utils.toast('error', `Failed to generate formula description: ${err.message}`)
                              setGeneratingDescription(false)
                            })
                          }
                          addDataType(savedDataType)
                          if (!isEditing) {
                            updateTrial(trial._id, { dataTypeCount: (trial.dataTypeCount ?? 1) + 1 })
                          }
                        })
                      }}
                      actionColor='primary'
                    />}
                </Stack>
                {formulaDescription &&
                  <Stack gap={2} p={3} bg='bg.subtle' rounded='md' borderWidth='1px' borderColor='border.emphasized'>
                    <Text fontWeight='bold' fontSize='sm'>Formula description:</Text>
                    <Text fontSize='sm' whiteSpace='pre-wrap'>{formulaDescription}</Text>
                    <Text fontSize='xs' color='fg.muted'>Remember that AI can make mistakes.</Text>
                  </Stack>}
              </Card>}

            <Stack bg='bg.subtle' p={3} direction='row' shadow='md' rounded='md' justifyContent='space-between' alignItems='center' position='sticky' bottom='10px'>
              <Box>
                {error &&
                  <Alert status='error' variant='left-accent' title='There was a problem saving your data type'>
                    {error}. Please fix these issues and try again.
                  </Alert>}
              </Box>
              {dataType && dataType?.type !== selectedType &&
                <Alert size='sm' status='warning'>Changing the format of this data type can have unexpected side effects. Where possible, we recommend creating a new data type.</Alert>}
              <Stack direction='row'>
                <Button variant='ghost' onClick={() => setOpen(false)}>Cancel</Button>
                <Button colorPalette='primary' onClick={() => create()} isLoading={saving || (isEditing && loading)} disabled={isEditing && !dataType}>{isEditing ? 'Save data type' : 'Create data type'}</Button>
              </Stack>
            </Stack>
          </Stack>
}
      />
    </>
  )
}

const PromisFormPicker = memo(function PromisFormPicker ({
  promisForms,
  promisFormsLoading,
  promisFormOid,
  setPromisFormOid,
  promisQuery,
  setPromisQuery,
  setSelectedType
}) {
  const normalisedQuery = useMemo(
    () => promisQuery.trim().toLowerCase(),
    [promisQuery]
  )

  const filteredPromisForms = useMemo(
    () => normalisedQuery
      ? promisForms.filter(f =>
        f.Name?.toLowerCase().includes(normalisedQuery) ||
          f.OID?.toLowerCase().includes(normalisedQuery))
      : promisForms,
    [promisForms, normalisedQuery]
  )

  const rows = useMemo(
    () => filteredPromisForms.map(f => [
      <Icon
        key={`${f.OID}-icon`}
        color={promisFormOid === f.OID ? 'green.500' : 'fg.subtle'}
        cursor='pointer'
        onClick={() => { setSelectedType('promisForm'); setPromisFormOid(f.OID) }}
      >
        {promisFormOid === f.OID ? <FaCheck /> : <FaRegCircle />}
      </Icon>,
      <Text
        key={`${f.OID}-name`}
        fontSize='sm'
        cursor='pointer'
        fontWeight={promisFormOid === f.OID ? 'bold' : 'normal'}
        onClick={() => { setSelectedType('promisForm'); setPromisFormOid(f.OID) }}
      >
        {f.Name}
      </Text>,
      <Badge key={`${f.OID}-oid`} colorPalette='gray' size='xs'>{f.OID}</Badge>
    ]),
    [filteredPromisForms, promisFormOid, setPromisFormOid, setSelectedType]
  )

  return (
    <Box>
      <Input
        size='sm' mb={3} bg='bg.surface'
        placeholder='Search forms by name or OID…'
        value={promisQuery}
        onChange={e => setPromisQuery(e.target.value)}
      />
      {promisFormsLoading
        ? <Text fontSize='sm' color='fg.muted'>Loading PROMIS forms…</Text>
        : filteredPromisForms.length === 0
          ? <Text fontSize='sm' color='fg.muted'>{promisQuery ? 'No forms match your search.' : 'No PROMIS forms available.'}</Text>
          : (
            <Box maxH='300px' overflowY='auto'>
              <SimpleTable
                tableProps={{ size: 'sm' }}
                headers={['', 'Name', 'OID']}
                rows={rows}
              />
            </Box>
            )}
    </Box>
  )
})

const PromisBatteryPicker = memo(function PromisBatteryPicker ({
  promisBatteries,
  promisBatteriesLoading,
  promisBatteryOid,
  setPromisBatteryOid,
  promisBatteryQuery,
  setPromisBatteryQuery,
  setSelectedType
}) {
  const normalisedQuery = useMemo(
    () => promisBatteryQuery.trim().toLowerCase(),
    [promisBatteryQuery]
  )

  const filteredBatteries = useMemo(
    () => normalisedQuery
      ? promisBatteries.filter(b =>
        b.Name?.toLowerCase().includes(normalisedQuery) ||
          b.OID?.toLowerCase().includes(normalisedQuery))
      : promisBatteries,
    [promisBatteries, normalisedQuery]
  )

  const rows = useMemo(
    () => filteredBatteries.map(b => [
      <Icon
        key={`${b.OID}-icon`}
        color={promisBatteryOid === b.OID ? 'green.500' : 'fg.subtle'}
        cursor='pointer'
        onClick={() => { setSelectedType('promisBattery'); setPromisBatteryOid(b.OID) }}
      >
        {promisBatteryOid === b.OID ? <FaCheck /> : <FaRegCircle />}
      </Icon>,
      <Text
        key={`${b.OID}-name`}
        fontSize='sm'
        cursor='pointer'
        fontWeight={promisBatteryOid === b.OID ? 'bold' : 'normal'}
        onClick={() => { setSelectedType('promisBattery'); setPromisBatteryOid(b.OID) }}
      >
        {b.Name}
      </Text>,
      <Badge key={`${b.OID}-oid`} colorPalette='gray' size='xs'>{b.OID}</Badge>
    ]),
    [filteredBatteries, promisBatteryOid, setPromisBatteryOid, setSelectedType]
  )

  return (
    <Box>
      <Input
        size='sm' mb={3} bg='bg.surface'
        placeholder='Search batteries by name or OID…'
        value={promisBatteryQuery}
        onChange={e => setPromisBatteryQuery(e.target.value)}
      />
      {promisBatteriesLoading
        ? <Text fontSize='sm' color='fg.muted'>Loading PROMIS batteries…</Text>
        : filteredBatteries.length === 0
          ? <Text fontSize='sm' color='fg.muted'>{promisBatteryQuery ? 'No batteries match your search.' : 'No PROMIS batteries available.'}</Text>
          : (
            <Box maxH='300px' overflowY='auto'>
              <SimpleTable
                tableProps={{ size: 'sm' }}
                headers={['', 'Name', 'OID']}
                rows={rows}
              />
            </Box>
            )}
    </Box>
  )
})

export default NewData
